import React from 'react';
import ReactQuill from 'react-quill';
import makeStyles from '@mui/styles/makeStyles';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import { Box, InputLabel, Stack, Typography } from '@mui/material';
import { AftermarketProductCatalogDetails } from '../../api/types';

const productCatalogQuillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
  ],
};

interface Props {
  canWrite?: boolean;
  value: AftermarketProductCatalogDetails;
  onChange: <K extends keyof AftermarketProductCatalogDetails>(
    key: K,
    value: AftermarketProductCatalogDetails[K]
  ) => void;
}

export const AftermarketProductCatalogForm = ({ canWrite = false, value, onChange }: Props) => {
  const classes = useStyles();

  return (
    <Stack gap="1rem">
      <Typography variant="subtitle1" color="primary">
        <LooksOneIcon /> Product Catalog Information
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <Box>
          <InputLabel id={'guidelines'} className={classes.catalogInformationLabel}>
            Product Summary
          </InputLabel>
          <ReactQuill
            className={classes.catalogInformationQuillInput}
            theme="snow"
            value={value.productSummary}
            onChange={(content, delta, source, editor) => {
              const currentDelta = editor.getContents();
              onChange('productSummary', currentDelta as any as string);
            }}
            modules={productCatalogQuillModules}
            readOnly={!canWrite}
          />
        </Box>

        <Box>
          <InputLabel id={'guidelines'} className={classes.catalogInformationLabel}>
            Limitations of Liability
          </InputLabel>
          <ReactQuill
            className={classes.catalogInformationQuillInput}
            theme="snow"
            value={value.limitationsOfLiability}
            onChange={(content, delta, source, editor) => {
              const currentDelta = editor.getContents();
              onChange('limitationsOfLiability', currentDelta as any as string);
            }}
            modules={productCatalogQuillModules}
            readOnly={!canWrite}
          />
        </Box>

        <Box>
          <InputLabel id={'guidelines'} className={classes.catalogInformationLabel}>
            Eligibility & Inclusions
          </InputLabel>
          <ReactQuill
            className={classes.catalogInformationQuillInput}
            theme="snow"
            value={value.eligibilityAndInclusions}
            onChange={(content, delta, source, editor) => {
              const currentDelta = editor.getContents();
              onChange('eligibilityAndInclusions', currentDelta as any as string);
            }}
            modules={productCatalogQuillModules}
            readOnly={!canWrite}
          />
        </Box>

        <Box>
          <InputLabel id={'guidelines'} className={classes.catalogInformationLabel}>
            Legal Disclaimer
          </InputLabel>
          <ReactQuill
            className={classes.catalogInformationQuillInput}
            theme="snow"
            value={value.legalDisclaimer}
            onChange={(content, delta, source, editor) => {
              const currentDelta = editor.getContents();
              onChange('legalDisclaimer', currentDelta as any as string);
            }}
            modules={productCatalogQuillModules}
            readOnly={!canWrite}
          />
        </Box>

        <Box>
          <InputLabel id={'guidelines'} className={classes.catalogInformationLabel}>
            Paragraph Description
          </InputLabel>
          <ReactQuill
            className={classes.catalogInformationQuillInput}
            theme="snow"
            value={value.paragraphDescription}
            onChange={(content, delta, source, editor) => {
              const currentDelta = editor.getContents();
              onChange('paragraphDescription', currentDelta as any as string);
            }}
            modules={productCatalogQuillModules}
            readOnly={!canWrite}
          />
        </Box>

        <Box>
          <InputLabel id={'guidelines'} className={classes.catalogInformationLabel}>
            Coverage Details
          </InputLabel>
          <ReactQuill
            theme="snow"
            value={value.coverageDetails}
            className={classes.catalogInformationQuillInput}
            onChange={(content, delta, source, editor) => {
              const currentDelta = editor.getContents();
              onChange('coverageDetails', currentDelta as any as string);
            }}
            modules={productCatalogQuillModules}
            readOnly={!canWrite}
          />
        </Box>
      </Box>
    </Stack>
  );
};

const useStyles = makeStyles({
  catalogInformationLabel: {
    fontWeight: 'bold',
    fontSize: '18px',
  },
  catalogInformationQuillInput: {
    '& .ql-editor': {
      minHeight: '300px', // Set your desired minimum height for the content area
    },
  },
});
